import { useCallback, useMemo, useState } from 'react';
import { useAuth } from '../utils/AuthContext';
import PriceReportDialog from './PriceReportDialog';
import { basket, useBasket } from '../utils/basketStore';
import { favorites, useFavoriteIds } from '../utils/favoritesStore';

const formatPrice = (n) => `₪${Number(n).toFixed(2)}`;

// 30.09.2026 10:19
const pad = (n) => String(n).padStart(2, '0');
const formatUpdated = (value) => {
  if (!value) return null;
  const d = new Date(value);
  return `${pad(d.getDate())}.${pad(d.getMonth() + 1)}.${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

// הכתובת מה-API של הפלטפורמה היא תבנית: .../{{size}}.{{extension||'jpg'}}
const imageSrc = (url) => url ? url.replace('{{size}}', 'medium').replace(/\{\{extension[^}]*\}\}/, 'jpg') : null;

// קישור לדף המוצר באתר החנות. item.id = platformProductId, אותו מספר בכל החנויות (ר' productUrlTemplate ב-Tables.sql)
const productUrl = (store, productId) => store.productUrlTemplate.replace('{productId}', encodeURIComponent(productId));

// אותו חישוב כמו spInnerFillItemPrices / spSearchProducts (שלב 5) — אחרי עדכון מחיר בשורה, בלי לטעון מחדש
const computeStats = (stores, prices) => {
  const list = stores.map((st) => prices[st.id] && { storeId: st.id, price: Number(prices[st.id].price) }).filter(Boolean);
  if (list.length < 2) return { storeCount: list.length, priceDiff: 0, diffPct: null, cheapestStoreId: null };
  const min = Math.min(...list.map((x) => x.price));
  const max = Math.max(...list.map((x) => x.price));
  const cheapest = list.filter((x) => x.price === min).sort((a, b) => a.storeId - b.storeId)[0];
  return {
    storeCount: list.length,
    priceDiff: Math.round((max - min) * 100) / 100,
    diffPct: min > 0 ? Math.round(((max - min) * 1000) / min) / 10 : null,
    cheapestStoreId: max > min ? cheapest.storeId : null,
  };
};

const s = {
  row:       { display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 14, background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, padding: 12 },
  imageBox:  { width: 72, height: 72, flex: '0 0 72px', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#fafafa', borderRadius: 8, overflow: 'hidden' },
  image:     { maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' },
  noImage:   { fontSize: 11, color: '#8c959f' },
  info:      { flex: '1 1 220px', minWidth: 0 },
  name:      { fontWeight: 600, fontSize: 16 },
  meta:      { color: '#59636e', fontSize: 13, marginTop: 2 },
  rowBadge:  { display: 'inline-block', marginInlineEnd: 8, padding: '1px 8px', borderRadius: 6, fontSize: 13, fontWeight: 700, verticalAlign: 'middle' },
  badgeDeal: { color: '#fff', background: '#cf222e' },
  badgeDrop: { color: '#1a7f37', background: '#dafbe1' },
  dropTag:   { alignSelf: 'flex-start', marginTop: 4, fontSize: 12, fontWeight: 600, color: '#1a7f37', background: '#dafbe1', borderRadius: 6, padding: '2px 6px' },
  itemActions: { display: 'flex', alignItems: 'center', gap: 6, marginTop: 8 },
  favBtn:    { width: 32, height: 30, padding: 0, fontSize: 15, lineHeight: 1, background: '#fff', border: '1px solid #d0d7de', borderRadius: 8 },
  favOn:     { borderColor: '#ff8182', background: '#fff0f0' },
  basketBtn: { padding: '5px 10px', fontSize: 13, fontWeight: 600, color: '#1f2328', background: '#fff', border: '1px solid #d0d7de', borderRadius: 8 },
  inBasket:  { color: '#1a7f37', borderColor: '#2da44e', background: '#effaf2' },
  prices:    { display: 'flex', flexWrap: 'wrap', gap: 8, flex: '1 1 260px' },
  // עמודה: הכפתורים נדחפים לתחתית (marginTop: auto), ובכל החנויות הם באותו גובה
  store:     { flex: '1 1 150px', minWidth: 140, border: '1px solid #e5e7eb', borderRadius: 10, padding: '8px 10px', background: '#fff', display: 'flex', flexDirection: 'column' },
  cheapest:  { borderColor: '#2da44e', background: '#effaf2' },
  missing:   { background: '#f6f8fa', color: '#8c959f' },
  storeName: { fontSize: 13, color: '#59636e', display: 'flex', justifyContent: 'space-between', gap: 6 },
  badge:     { fontSize: 11, fontWeight: 600, color: '#1a7f37' },
  price:     { fontSize: 20, fontWeight: 700 },
  oldPrice:  { fontSize: 13, color: '#8c959f', textDecoration: 'line-through', lineHeight: 1.2 },
  reported:  { alignSelf: 'flex-start', marginTop: 2, fontSize: 12, fontWeight: 600, color: '#1a3f9c', background: '#eef4ff', borderRadius: 6, padding: '2px 6px' },
  sitePrice: { fontSize: 12, color: '#59636e', marginTop: 2 },
  updated:   { fontSize: 11, color: '#8c959f', marginTop: 4 },
  perKg:     { fontSize: 12, fontWeight: 400, color: '#59636e', marginInlineStart: 4 },
  promo:     { alignSelf: 'flex-start', marginTop: 4, fontSize: 12, color: '#9a6700', background: '#fff8c5', borderRadius: 6, padding: '2px 6px', display: 'inline-block', maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  // שורת הכפתורים — בתחתית, צמודה לשמאל (flex-end ב-RTL)
  actions:   { display: 'flex', flexWrap: 'wrap', justifyContent: 'flex-end', gap: 6, marginTop: 'auto', paddingTop: 8 },
  storeLink: { padding: '6px 12px', fontSize: 13, fontWeight: 600, color: '#fff', background: '#1a3f9c', borderRadius: 8, textDecoration: 'none', whiteSpace: 'nowrap' },
  reportBtn: { padding: '5px 10px', fontSize: 13, fontWeight: 600, color: '#1a3f9c', background: '#fff', border: '1px solid #1a3f9c', borderRadius: 8, whiteSpace: 'nowrap' },
  diff:      { flex: '0 0 120px', textAlign: 'center' },
  diffValue: { fontSize: 18, fontWeight: 700, color: '#cf222e' },
  diffPct:   { fontSize: 13, color: '#59636e' },
  diffNone:  { fontSize: 13, color: '#59636e' },
};

// שורת מוצר: המחיר בכל סופר, הזול מסומן, וההפרש. משתמש מחובר יכול לעדכן מחיר (PriceReportDialog).
// מחיר שעודכן ע"י משתמש (p.reportedPrice) הוא המחיר להשוואה (p.price), והמחיר מהאתר (p.sitePrice) מוצג לצידו.
// רשות: badge — { text, tone: 'deal' | 'drop' } ליד השם ("-25%" במבצעים, "ירד מחיר!" במועדפים);
//       drops — { [storeId]: { oldPrice } } → "ירד מ-₪X" בכרטיס החנות (spGetFavorites [5])
const ProductRow = ({ item, stores, prices, badge, drops }) => {
  const { requireLogin } = useAuth();
  const basketItems = useBasket();
  const inBasket = basketItems.some((x) => x.id === item.id);
  const favoriteIds = useFavoriteIds();
  const isFavorite = favoriteIds.has(item.id);
  const [imageFailed, setImageFailed] = useState(false);
  const [overrides, setOverrides] = useState({});         // storeId → תוצאת spReportPrice אחרי עדכון מהשורה הזו
  const [reportStoreId, setReportStoreId] = useState(null);
  const closeReport = useCallback(() => setReportStoreId(null), []);
  const src = imageSrc(item.imageUrl);

  // המחירים אחרי עדכונים מקומיים, והסיכום (הזול / הפרש) — מחושב מחדש רק אם היה עדכון בשורה
  const mergedPrices = useMemo(() => {
    if (!Object.keys(overrides).length) return prices;
    const merged = { ...prices };
    Object.entries(overrides).forEach(([storeId, o]) => { merged[storeId] = { ...merged[storeId], ...o }; });
    return merged;
  }, [prices, overrides]);
  const stats = useMemo(
    () => (Object.keys(overrides).length ? computeStats(stores, mergedPrices) : item),
    [overrides, stores, mergedPrices, item],
  );

  const meta = [
    item.brandName,
    item.isWeighable ? 'שקיל' : (item.weight && item.unitName ? `${Number(item.weight)} ${item.unitName}` : null),
    item.barcode ? `ברקוד ${item.barcode}` : null,
  ].filter(Boolean).join(' · ');

  const onlyStore = stats.storeCount === 1 ? stores.find((st) => mergedPrices[st.id]) : null;
  const reportStore = reportStoreId ? stores.find((st) => st.id === reportStoreId) : null;

  const openReport = (storeId) =>
    requireLogin('כדי לעדכן מחיר צריך להתחבר', () => setReportStoreId(storeId));

  const toggleFavorite = () => requireLogin('כדי לשמור מועדפים צריך להתחבר', async () => {
    const result = await favorites.toggle(item.id);
    if (!result.ok && result.msg) window.alert(result.msg);
  });

  const onReported = (result) => {
    setOverrides((prev) => ({
      ...prev,
      [reportStoreId]: { price: result.price, sitePrice: result.sitePrice, reportedPrice: result.reportedPrice, reportedAt: result.reportedAt },
    }));
    setReportStoreId(null);
  };

  return (
    <div style={s.row}>
      <div style={s.imageBox}>
        {src && !imageFailed
          ? <img style={s.image} src={src} alt="" loading="lazy" onError={() => setImageFailed(true)} />
          : <span style={s.noImage}>אין תמונה</span>}
      </div>

      <div style={s.info}>
        <div style={s.name}>
          {badge && <span style={{ ...s.rowBadge, ...(badge.tone === 'deal' ? s.badgeDeal : s.badgeDrop) }}>{badge.text}</span>}
          {item.name}
        </div>
        {meta && <div style={s.meta}>{meta}</div>}
        <div style={s.itemActions}>
          {/* רשימת קניות (basketStore) — לחיצה נוספת מסירה */}
          <button
            type="button"
            style={{ ...s.basketBtn, ...(inBasket ? s.inBasket : null) }}
            onClick={() => (inBasket
              ? basket.remove(item.id)
              : requireLogin('רשימת הקניות למשתמשים רשומים — נא להתחבר', () => basket.add(item)))}
            title={inBasket ? 'לחיצה מסירה מהרשימה' : undefined}
          >
            {inBasket ? '✓ ברשימת הקניות' : '➕ לרשימת קניות'}
          </button>
          {/* מועדפים (favoritesStore, נשמר בשרת) — מוצגים בעמוד הפרופיל */}
          <button
            type="button"
            style={{ ...s.favBtn, ...(isFavorite ? s.favOn : null) }}
            onClick={toggleFavorite}
            aria-pressed={isFavorite}
            aria-label={isFavorite ? `הסרת ${item.name} מהמועדפים` : `הוספת ${item.name} למועדפים`}
            title={isFavorite ? 'במועדפים — לחיצה מסירה' : 'הוספה למועדפים'}
          >
            {isFavorite ? '❤️' : '🤍'}
          </button>
        </div>
      </div>

      <div style={s.prices}>
        {stores.map((st) => {
          const p = mergedPrices[st.id];
          if (!p) {
            return (
              <div key={st.id} style={{ ...s.store, ...s.missing }}>
                <div style={s.storeName}>{st.name}</div>
                <div>לא נמכר</div>
              </div>
            );
          }
          const isCheapest = stats.cheapestStoreId === st.id;
          const isReported = p.reportedPrice != null;
          // עודכן: עדכון משתמש אם יש, אחרת הפעם האחרונה שהמחיר נקרא מאתר החנות
          const updatedAt = formatUpdated(isReported ? p.reportedAt : p.lastSeen);
          return (
            <div key={st.id} style={{ ...s.store, ...(isCheapest ? s.cheapest : null) }}>
              <div style={s.storeName}>
                <span>{st.name}</span>
                {isCheapest && <span style={s.badge}>הכי זול</span>}
              </div>
              {/* p.price = המחיר להשוואה: עדכון משתמש, אחרת מבצע אם יש, אחרת רגיל. ר' effectivePrice ב-Tables.sql */}
              {!isReported && p.regularPrice > p.price && (
                <div style={s.oldPrice} className="num" aria-label={`במקום ${formatPrice(p.regularPrice)}`}>
                  {formatPrice(p.regularPrice)}
                </div>
              )}
              <div style={s.price}>
                <span className="num">{formatPrice(p.price)}</span>
                {item.isWeighable && <span style={s.perKg}>לק"ג</span>}
              </div>
              {isReported && (
                <>
                  <span style={s.reported}>עודכן ע"י משתמש</span>
                  {p.sitePrice != null && Number(p.sitePrice) !== Number(p.price) && (
                    <div style={s.sitePrice}>מחיר באתר החנות: <span className="num">{formatPrice(p.sitePrice)}</span></div>
                  )}
                </>
              )}
              {drops?.[st.id] && (
                <span style={s.dropTag}>⬇ ירד מ-<span className="num">{formatPrice(drops[st.id].oldPrice)}</span></span>
              )}
              {p.promotionTag && <div style={s.promo} title={p.promotionTag}>{p.promotionTag}</div>}
              {updatedAt && <div style={s.updated}>עודכן: <span className="num">{updatedAt}</span></div>}

              <div style={s.actions}>
                {p.productId && (
                  <button type="button" style={s.reportBtn} onClick={() => openReport(st.id)}>עדכון מחיר</button>
                )}
                {st.productUrlTemplate && (
                  <a
                    style={s.storeLink}
                    href={productUrl(st, item.id)}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={`${item.name} באתר ${st.name}`}
                  >
                    מעבר למוצר
                  </a>
                )}
              </div>
            </div>
          );
        })}
      </div>

      <div style={s.diff}>
        {onlyStore ? (
          <div style={s.diffNone}>נמכר רק ב{onlyStore.name}</div>
        ) : stats.priceDiff > 0 ? (
          <>
            <div style={s.diffValue} className="num">{formatPrice(stats.priceDiff)}</div>
            <div style={s.diffPct}>הפרש של {Number(stats.diffPct)}%</div>
          </>
        ) : (
          <div style={s.diffNone}>אותו מחיר</div>
        )}
      </div>

      {reportStore && (
        <PriceReportDialog
          item={item}
          store={reportStore}
          price={mergedPrices[reportStore.id]}
          onDone={onReported}
          onClose={closeReport}
        />
      )}
    </div>
  );
};

export default ProductRow;
